import { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Image, ActivityIndicator } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ArrowRight, BarChart3, Shield, CheckCircle } from 'lucide-react-native';
import Animated, { FadeInUp, SlideInRight, FadeIn, useSharedValue, useAnimatedStyle, withRepeat, withTiming, Easing } from 'react-native-reanimated';
import { isWeb } from '@/lib/platform';
import { isBetaMode, mode } from '@/lib/config/app-mode';
import { BetaLandingPage } from '@/components/BetaLandingPage';
import { LaunchLandingPage } from '@/components/LaunchLandingPage';
import { useAuth } from '@/lib/supabase';
import { useProfileStore } from '@/lib/state/profile-store';
import { resolveEffectiveRole } from '@/lib/auth/effective-role';
import { useIsAdmin } from '@/lib/auth/role';
import { shouldEnterApp } from '@/lib/onboarding/should-enter-app';
import { shouldGateEmailConfirmation } from '@/lib/onboarding/email-gate';
import { resolveOnboardingRoute } from '@/lib/onboarding/resolve-route';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { LOGO_MARK, LOGO_WORDMARK_WHITE } from '@/lib/logo';

// ============================================================================
// Splash View (shown during auth/hydration loading)
// ============================================================================

function SplashView() {
  return (
    <View className="flex-1 items-center justify-center" style={{ backgroundColor: '#0A1628' }}>
      <ActivityIndicator size="large" color="#00D9D9" />
    </View>
  );
}

// ============================================================================
// Native Landing Screen (branded entry — no role selection)
// ============================================================================

function NativeLandingScreen({ router, insets }: { router: ReturnType<typeof useRouter>; insets: ReturnType<typeof useSafeAreaInsets> }) {
  // Breathing pulse animation for logo
  const pulseScale = useSharedValue(1);
  useEffect(() => {
    pulseScale.value = withRepeat(
      withTiming(1.06, { duration: 2000, easing: Easing.inOut(Easing.ease) }),
      -1,
      true,
    );
  }, []);
  const pulseStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pulseScale.value }],
  }));

  const VALUE_PROPS = [
    { icon: BarChart3, label: 'AI-powered investment scoring' },
    { icon: Shield, label: 'Secure data room management' },
    { icon: CheckCircle, label: 'Verified investor matching' },
  ] as const;

  return (
    <LinearGradient
      colors={['#0A1628', '#132240', '#1A3058']}
      start={{ x: 0.2, y: 0 }}
      end={{ x: 0.8, y: 1 }}
      style={{ flex: 1, paddingTop: insets.top, paddingBottom: insets.bottom }}
    >
      <View className="flex-1 px-6 justify-between">
        {/* Top: Logo + Wordmark + Tagline */}
        <Animated.View
          entering={FadeIn.delay(200).duration(800)}
          className="items-center mt-12"
        >
          <Animated.View style={pulseStyle}>
            <Image
              source={LOGO_MARK}
              style={{ width: 88, height: 88 }}
              resizeMode="contain"
              accessibilityRole="image"
              accessibilityLabel="Inverio logo"
            />
          </Animated.View>
          <Image
            source={LOGO_WORDMARK_WHITE}
            style={{ width: 170, height: 44, marginTop: 14 }}
            resizeMode="contain"
            accessibilityRole="image"
            accessibilityLabel="Inverio wordmark"
          />
          <Text className="text-center text-base mt-4" style={{ color: 'rgba(255,255,255,0.8)' }}>
            AI-powered investment readiness{'\n'}for startups & angel investors
          </Text>
        </Animated.View>

        {/* Middle: Value Propositions */}
        <View className="gap-4 my-6">
          {VALUE_PROPS.map((item, i) => (
            <Animated.View
              key={i}
              entering={SlideInRight.delay(500 + i * 150).duration(500)}
              className="flex-row items-center gap-4"
            >
              <View
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', width: 44, height: 44, borderRadius: 12 }}
                className="items-center justify-center"
              >
                <item.icon size={22} color="#00D9D9" />
              </View>
              <Text className="text-base font-medium flex-1" style={{ color: 'rgba(255,255,255,0.9)' }}>
                {item.label}
              </Text>
            </Animated.View>
          ))}
        </View>

        {/* Bottom: Get Started + Sign In */}
        <View className="gap-3 mb-4">
          {/* Get Started — primary CTA */}
          <Animated.View entering={FadeInUp.delay(900).duration(600)}>
            <AnimatedPressable onPress={() => router.push('/login')} accessibilityRole="button" accessibilityLabel="Get started">
              <View
                style={{
                  backgroundColor: '#00D9D9',
                  borderRadius: 16,
                  paddingVertical: 16,
                  shadowColor: '#00D9D9',
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 12,
                  elevation: 4,
                }}
              >
                <View className="flex-row items-center justify-center gap-2">
                  <Text className="text-base font-bold" style={{ color: '#0A1628' }}>
                    Get Started
                  </Text>
                  <ArrowRight size={18} color="#0A1628" />
                </View>
              </View>
            </AnimatedPressable>
          </Animated.View>

          {/* Sign In Link */}
          <Animated.View
            entering={FadeInUp.delay(1050).duration(600)}
            className="items-center mt-2"
          >
            <AnimatedPressable onPress={() => router.push('/login')} accessibilityRole="link" accessibilityLabel="Sign in to existing account" className="py-2">
              <Text className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Already have an account?{' '}
                <Text className="font-semibold underline" style={{ color: '#FFFFFF' }}>
                  Sign In
                </Text>
              </Text>
            </AnimatedPressable>
          </Animated.View>

          {/* Footer Trust Text */}
          <Animated.View
            entering={FadeIn.delay(1200).duration(600)}
            className="items-center mt-1 mb-2"
          >
            <Text className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Powered by AI · Built in Geneva, Switzerland
            </Text>
          </Animated.View>
        </View>
      </View>
    </LinearGradient>
  );
}

// ============================================================================
// Landing Screen (declarative routing decision point)
// ============================================================================

export default function LandingScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const isFocused = useIsFocused();
  const { user, profile, isLoading: authLoading, emailConfirmed } = useAuth();
  const hydrated = useProfileStore((s) => s._hasHydrated);
  const userRole = useProfileStore((s) => s.userRole);
  const setUserRole = useProfileStore((s) => s.setUserRole);
  const startupOnboarding = useProfileStore((s) => s.startupOnboarding);
  const investorOnboarding = useProfileStore((s) => s.investorOnboarding);
  const hasNavigated = useRef(false);

  // Existing-startup founder resolver (Task 4): resolves the caller's startup via
  // ACTIVE team_members membership → teams.startup_id, with a legacy
  // startup_profiles.user_id fallback (see resolveMyStartup). A returning owner —
  // including the legacy user_id-only owner with 0 team_members (smoke@ persona,
  // pre-backfill) — must be surfaced into their workspace, not dropped into the
  // "create a company" flow. Primitives extracted to keep the redirect useMemo's
  // deps stable. In mock mode there is no Supabase session, so this resolves to
  // no-startup fast and never gates mock routing.
  const myStartup = useMyStartup();
  const myStartupId = myStartup.startupId;
  const myStartupIsInvitee = myStartup.isInvitee;
  const myStartupLoading = myStartup.isLoading;


  // SSG mount guard: server renders the splash, client renders the splash on first paint,
  // and only after `mounted` flips true does the redirect/landing logic run. Prevents
  // React hydration error #418 caused by client-only state (auth, persisted store,
  // window dimensions) producing a different tree than the static HTML.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Mock mode: userRole alone is enough (no Supabase session).
  // Demo/production: require a real user session alongside the role.
  const isMockMode = mode.isMock;
  const hasAuth = !!user || isMockMode;

  // ADMINISTRATIVE AUTHORITY — asked BEFORE persona, and answered by the one
  // table that decides it (`public.admin_roles`). The same hook and React Query
  // key the /(admin) guard uses, so the router and the guard cannot disagree.
  // Routing on `profiles.role` sent a persona-only "admin" into a shell that
  // refused them and bounced to /login, which bounced straight back here — an
  // infinite redirect loop presenting as the generic error screen (staging,
  // 2026-08-07). See .planning/ADMIN-AUTHORITY-MODEL.md.
  //
  // Mock mode has no database, so the dev role toggle remains authoritative
  // there and is the only place a store value may confer the admin shell.
  const { isAdmin: hasAdminAuthority, isLoading: adminAuthorityLoading } = useIsAdmin(user?.id);
  const isAdmin = isMockMode ? userRole === 'admin' : hasAdminAuthority;

  // When we have a real DB profile with an actual role, it is the source of truth.
  // Sync the store so persisted userRole stays consistent with the server.
  // Skip 'pending' — that's a placeholder, not a real role.
  // Extract stable primitives from profile to avoid re-renders on object reference changes
  const profileRole = profile?.role as string | undefined;
  const profileOnboardingCompleted = !!profile?.onboarding_completed;
  const profileOnboardingDeferred = !!profile?.onboarding_deferred;
  const hasProfile = !!profile;

  useEffect(() => {
    // In mock/demo-bypass mode, handleTryDemo sets the role directly — don't
    // let a stale DB profile overwrite it (e.g. admin demo has 'startup' in DB).
    if (isMockMode) return;

    // The store caches the resolved AUTHORIZATION role, not the raw persona
    // column. ~34 presentational call sites across the app read
    // `useProfileStore(s => s.userRole)` to decide which navigation, labels and
    // breadcrumbs to render. Under the single model `profiles.role` is never
    // 'admin', so mirroring the column verbatim would silently render the
    // startup/investor chrome around the admin shell for every administrator.
    //
    // This is a CACHE OF A DERIVED VALUE, not a second store of the fact:
    // authority still comes only from `admin_roles` via useIsAdmin(), it is
    // never written here, and nothing authorizes on this value — the route
    // guards read the server hook directly (src/lib/auth/role.ts), and the
    // store is client-writable, which is precisely why it must never gate.
    const authorizationRole = isAdmin ? 'admin' : profileRole;
    if (
      authorizationRole &&
      authorizationRole !== 'pending' &&
      authorizationRole !== userRole
    ) {
      setUserRole(authorizationRole as any);
    }
  }, [profileRole, isMockMode, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute redirect destination (null = stay on landing)
  // Dependencies are ALL primitives — no object references that change on every auth setState
  const redirectTo = useMemo(() => {
    if (!mounted || authLoading || !hydrated) return null;
    // Never route while administrative authority is unresolved: deciding on a
    // not-yet-loaded `false` would send a real administrator down the persona
    // path on every cold start and then visibly correct itself. Real sessions
    // only — mock mode has no membership to wait for.
    if (!!user && adminAuthorityLoading) return null;

    // ACCESS GATE (onboarding redesign D2 / proposal §7): a signed-in user whose
    // email is genuinely unconfirmed must confirm before reaching ANY onboarding/
    // app/admin route. This is evaluated BEFORE role/onboarding routing below.
    //
    // SAFETY — naturally inert when confirmation is OFF: `emailConfirmed` is derived
    // from `email_confirmed_at`, which Supabase sets immediately at signup when
    // confirmation is disabled (current project state). So every existing/confirmed
    // session has `emailConfirmed=true` and this returns false — no lockout. Only a
    // genuinely-null `email_confirmed_at` gates. Mock/demo sessions never gate.
    // `email_confirmed_at` lives on the auth user object (arrives with the session),
    // so there's no async window that could momentarily read a confirmed user as
    // unconfirmed — the authLoading bail above already covers session hydration.
    if (shouldGateEmailConfirmation({ hasUser: !!user, isMockMode, emailConfirmed })) {
      return '/confirm-email';
    }

    // DB profile is the source of truth for role. Local store is only used in mock mode
    // (no Supabase session). 'pending' means user hasn't chosen a role yet.
    // Single source of truth for role resolution (see effective-role.ts + its
    // test). Encodes the precedence that keeps a cold relaunch from bouncing a
    // returning user to /select-role while the async DB profile loads.
    // AUTHORITY BEFORE PERSONA. An administrator skips onboarding and persona
    // entirely — their `profiles.role` may legitimately be 'pending'.
    if (isAdmin) {
      return '/(admin)/admin-dashboard';
    }

    const effectiveRole = resolveEffectiveRole({
      hasAuth,
      isMockMode,
      hasUser: !!user,
      profileRole,
      persistedRole: userRole,
    });
    const onboarding = effectiveRole === 'startup' ? startupOnboarding : investorOnboarding;
    const isOnboarded = onboarding?.completedAt != null || profileOnboardingCompleted;

    // May-enter also covers the soft-gate: a user who hit Save & Exit / Skip for
    // now has `onboarding_deferred=true` and must be treated like an onboarded
    // user for routing purposes — otherwise this exact guard bounces them straight
    // back into onboarding, producing the exit loop `shouldEnterApp` exists to fix.
    const mayEnter =
      isOnboarded ||
      shouldEnterApp({
        onboardingCompleted: profileOnboardingCompleted,
        onboardingDeferred: profileOnboardingDeferred,
        hasProfile,
      });

    // Authenticated + onboarded (or soft-gate deferred) → dashboard
    if (effectiveRole && mayEnter) {
      return effectiveRole === 'startup'
        ? '/(startup)/startup-dashboard'
        : '/(investor)/investor-dashboard';
    }

    // Authenticated + has role + not onboarded (and not deferred) → onboarding
    //
    // Race-condition gate: only redirect if we have actually loaded the DB
    // profile (or are in mock/demo mode where local store is authoritative).
    // Without this gate, `profile` is undefined for ~200-500ms after login
    // while React Query fetches it; during that window `profileOnboardingCompleted`
    // is false, the redirect fires, and the user is stranded on /{role}-welcome
    // even though their DB profile has onboarding_completed=true. Confirmed
    // 2026-05-13 on iOS TestFlight + local web with the demo investor account.
    if (hasAuth && effectiveRole && !mayEnter && (isMockMode || hasProfile)) {
      // Existing-startup founder (Task 4): don't decide the founder create-vs-
      // surface route until ownership has resolved. A legacy owner
      // (startup_profiles.user_id with 0 team_members) resolves to an existing
      // startup and must be surfaced to their workspace, not flashed into
      // "create a company" during the ownership-query window. Gate only for real
      // (non-mock) startup sessions; mock mode has no membership query to wait on
      // and its routing is store-driven.
      if (!isMockMode && effectiveRole === 'startup' && myStartupLoading) {
        return null; // stay on splash until ownership resolves
      }

      // A founder who resolves to an established, non-invitee startup owns an
      // existing company — the "create/claim company" onboarding is behind them.
      // Invitees are excluded (their personal step is still required); mock mode
      // resolves to no-startup so this stays false there.
      const ownsExistingStartup =
        effectiveRole === 'startup' && myStartupId != null && !myStartupIsInvitee;

      // isInvitee: this screen doesn't drive the invitee step list here, so we
      // keep the founder-flow default (false) for step resolution — an invitee
      // who lands here mid-flow is corrected to the invitee step list once their
      // membership resolves (e.g. after landing on `${role}-personal`, which is
      // also invitees' first step). The membership-derived `myStartupIsInvitee`
      // is used ONLY to keep a real invitee out of the owner-surfacing path above.
      const isInvitee = false;
      return resolveOnboardingRoute({
        hasAuth,
        role: effectiveRole,
        mayEnter,
        isMockMode,
        hasProfile,
        onboardingStep: profile?.onboarding_step ?? null,
        isInvitee,
        ownsExistingStartup,
      });
    }

    // Authenticated + no role (or no profile yet) → role selection
    // Same race gate: only force role-select once the profile fetch has
    // returned and confirmed no role is set.
    if (user && !effectiveRole && (isMockMode || hasProfile)) {
      return '/select-role';
    }

    return null;
  }, [mounted, authLoading, hydrated, hasAuth, emailConfirmed, isMockMode, profileRole, profileOnboardingCompleted, profileOnboardingDeferred, hasProfile, userRole, user, startupOnboarding, investorOnboarding, myStartupId, myStartupIsInvitee, myStartupLoading, isAdmin, adminAuthorityLoading]);

  // Navigate via useEffect — fires once per destination, ref prevents re-triggering.
  // Only navigate when this screen is focused — prevents background navigation races
  // when other screens (e.g. login) trigger Zustand state changes.
  useEffect(() => {
    if (redirectTo && !hasNavigated.current && isFocused) {
      hasNavigated.current = true;
      router.replace(redirectTo as any);
    }
  }, [redirectTo, isFocused]);

  // Reset navigation ref when user signs out (redirectTo becomes null)
  useEffect(() => {
    if (!redirectTo) {
      hasNavigated.current = false;
    }
  }, [redirectTo]);

  // Show splash until client mount, while loading, or while redirect is pending.
  // The `!mounted` check is what makes the static-export HTML match the client first paint.
  if (!mounted || authLoading || !hydrated || redirectTo) {
    return <SplashView />;
  }

  // Not authenticated — render landing page
  if (isWeb) {
    return isBetaMode() ? <BetaLandingPage /> : <LaunchLandingPage />;
  }

  // Native landing
  return <NativeLandingScreen router={router} insets={insets} />;
}
